import type { HeavenlyStem } from '../types/stems';
import type { FourPillars } from '../types/pillars';
import type { YearlyFortune, SamjaePhase } from '../types/fortune';
import type { YongSin } from '../types/strength';
import type { GyeokGukAnalysis } from '../types/gyeok-guk';
import { branchIndex, makePillar } from '../core/sexagenary';
import { getTenGod } from '../analysis/ten-gods';
import { analyzeFortuneRelations } from '../relations/fortune-relations';
import { getSamhapGroup, SAMJAE_SAL } from '../sinsal/definitions';

/** 삼재 단계 이름 (SAMJAE_SAL 인덱스 순서와 일치: 입→중→출) */
const SAMJAE_PHASE_NAMES: readonly SamjaePhase[] = ['입삼재', '중삼재', '출삼재'];

/**
 * 세운 삼재 판정.
 *
 * 출생 년지의 삼합 그룹에 따라 삼재 3년(입/중/출)이 정해진다.
 * 세운 년지가 이 3년 중 하나면 해당 단계를, 아니면 null을 반환한다.
 *
 * 삼합 그룹별 삼재:
 * - 申子辰 → 寅(입), 卯(중), 辰(출)
 * - 寅午戌 → 申, 酉, 戌
 * - 巳酉丑 → 亥, 子, 丑
 * - 亥卯未 → 巳, 午, 未
 *
 * @param birthYearBranch - 출생 년지 인덱스 (0~11)
 * @param fortuneYearBranch - 세운 년지 인덱스 (0~11)
 * @returns 삼재 단계 또는 null
 */
function getSamjaePhase(birthYearBranch: number, fortuneYearBranch: number): SamjaePhase | null {
  const samhapGroup = getSamhapGroup(birthYearBranch);
  const samjaeBranches = SAMJAE_SAL[samhapGroup];
  const idx = samjaeBranches.indexOf(fortuneYearBranch);
  if (idx === -1) return null;
  return SAMJAE_PHASE_NAMES[idx];
}

/**
 * 특정 연도의 세운을 계산한다.
 *
 * 해당 연도의 육십갑자 기둥을 사용한다.
 * 년주와 동일한 공식: (year - 4) % 60
 */
export function getYearlyFortune(
  dayMaster: HeavenlyStem, year: number,
  fourPillars?: FourPillars, noHour?: boolean,
  yongSin?: YongSin, gyeokGuk?: GyeokGukAnalysis,
): YearlyFortune {
  const sexagenaryIndex = ((year - 4) % 60 + 60) % 60;
  const pillar = makePillar(sexagenaryIndex);

  const samjae = fourPillars
    ? getSamjaePhase(branchIndex(fourPillars.year.branch), branchIndex(pillar.branch))
    : null;

  return {
    year,
    pillar,
    tenGod: getTenGod(dayMaster, pillar.stem),
    relations: fourPillars ? analyzeFortuneRelations(fourPillars, pillar, noHour, yongSin, gyeokGuk) : [],
    samjae,
  };
}

/**
 * 연도 범위의 세운을 계산한다.
 */
export function getYearlyFortuneRange(
  dayMaster: HeavenlyStem, startYear: number, count: number,
  fourPillars?: FourPillars, noHour?: boolean,
  yongSin?: YongSin, gyeokGuk?: GyeokGukAnalysis,
): YearlyFortune[] {
  const result: YearlyFortune[] = [];
  for (let i = 0; i < count; i++) {
    result.push(getYearlyFortune(dayMaster, startYear + i, fourPillars, noHour, yongSin, gyeokGuk));
  }
  return result;
}
